import React, { useEffect, useRef } from "react";

import { z } from "zod";

import { Icon, Modal } from "@braze/beacon-core";
import { useId } from "@braze/beacon-hooks";

import { useAgenticAiContext } from "@services/useAgenticAiContext";
import useI18nNamespace from "@services/useI18nNamespace";

import { DiffDisplay } from "@components/AgenticAi/components/DiffDisplay/DiffDisplay";

import type { BrazeModalProps } from "./types";

export const BrazeModal = ({
  isOpen,
  onOperatorClose,
  operatorLabel,
  children,
  ...props
}: BrazeModalProps): React.ReactElement => {
  const { t } = useI18nNamespace("braze-modal");
  const { registerTool } = useAgenticAiContext();
  const id = useId("braze-modal");

  const onOperatorCloseRef = useRef(onOperatorClose);
  onOperatorCloseRef.current = onOperatorClose;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const cleanup = registerTool({
      name: `close_modal_${id}`,
      description: `Close the modal with id ${id}`,
      parameters: z.object({}),
      callback: () => {
        onOperatorCloseRef.current();
        return "Modal was successfully closed.";
      },
      options: {
        toolGroup: "Other",
        labels: {
          ready: t("close-modal-ready", "Preparing to close modal"),
          running: t("close-modal-running", "Closing modal"),
          complete: t("close-modal-complete", "Modal closed"),
          failed: t("close-modal-failed", "Failed to close modal"),
          stopped: t("close-modal-stopped", "Stopped closing modal"),
        },
        requiresApproval: "yes",
        argumentsSummaryText: () => operatorLabel,
        renderApproval: () => {
          return (
            <DiffDisplay
              title={t("action-title", "Action")}
              modified={t("close-modal-description", "Close modal: {label}", { label: operatorLabel })}
            />
          );
        },
        toolIcon: <Icon name="times" />,
      },
    });

    return () => {
      cleanup();
    };
  }, [registerTool, id, t, isOpen, operatorLabel]);

  return (
    <Modal isOpen={isOpen} {...props} data-operator-id={id}>
      {children}
    </Modal>
  );
};
